import { config } from '@rematter/config';
import { AgentSortOption, AgentType } from '@rematter/frontend-lib-api-client';
import { maybe } from '@rematter/ui-web';
import { atom } from 'jotai';

import { agentListQueryAtom } from '../_graphql/queryAgentList';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $limit = atomFromQueryString.number('limit', config.pageSize);
export const $offset = atomFromQueryString.number('offset', 0);
export const $sortBy = atomFromQueryString.string<AgentSortOption>(
  'sortBy',
  'NameAsc',
);

// Type filter: 'all' | 'Agent' | 'SubAgent'
export const $typeFilter = atomFromQueryString.string<'all' | AgentType>(
  'type',
  'all',
);

// Active filter: undefined (all) | true (active) | false (inactive)
export const $activeFilter = atomFromQueryString.string<
  'all' | 'active' | 'inactive'
>('status', 'all');

// 🟢🟢🟢 REMOTE DATA (raw from backend)
export const $agentsRaw: ReturnType<typeof agentListQueryAtom> =
  agentListQueryAtom((get) => {
    const activeFilter = get($activeFilter);

    return {
      variables: {
        searchTerm: get($searchTerm),
        isActive:
          activeFilter === 'all'
            ? undefined
            : activeFilter === 'active'
              ? true
              : false,
        sortBy: get($sortBy),
        pagination: maybe(get($limit), (limit) => ({
          limit,
          offset: get($offset) ?? 0,
        })),
      },
    };
  });

// 🟢🟢🟢 COMPUTED DATA (with client-side type filtering)
export const $agents = atom((get) => {
  const rawData = get($agentsRaw);
  const typeFilter = get($typeFilter);

  // If no data yet, return as is
  if (!rawData.data) {
    return rawData;
  }

  // If showing all types, return raw data
  if (typeFilter === 'all') {
    return rawData;
  }

  // Apply client-side type filtering
  const filtered = (rawData.data.agents ?? []).filter(
    (agent: { type: AgentType }) => agent.type === typeFilter,
  );

  return {
    ...rawData,
    data: {
      agents: filtered,
      total: filtered.length,
    },
  };
});
